import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AccountPreferences,
  MFAStatus,
  EventData,
  disableMFA,
  getAccountPreferences,
  getMFAStatus,
  getTenantSubscriptionStatus,
  listEvents,
  regenerateRecoveryCodes,
  TenantSubscriptionStatus,
  updateAccountPreferences,
  updateProfile,
} from '../services/api'
import { useAuthStore } from '../store/authStore'
import { errorText } from '../utils/errorText'
import { dateTime } from '../utils/date'
import ChangePasswordModal from '../components/ChangePasswordModal'
import MfaSetupModal from '../components/MfaSetupModal'
import { Avatar, Badge, Field, Modal, PageHeader, PageShell, SectionCard, Spinner, Toggle } from '../components/ui'

/**
 * My Account — the profile, security, notification and tenant panels the signed-in user owns.
 *
 * Everything here acts on the caller: `/auth/account/*` and `/auth/mfa/*` take no user id, so the
 * page cannot reach another person's account whatever the role. Administering OTHER users is a
 * different screen (Teams > Users) with a different permission.
 */

/**
 * Timezones offered. A short, hand-picked list rather than the full IANA database: this only
 * decides how timestamps are labelled, and a 400-entry select is worse than a covering handful.
 */
const TIMEZONES = [
  { value: 'UTC', label: '(UTC) Coordinated Universal Time' },
  { value: 'America/Los_Angeles', label: '(UTC-08:00) Pacific Time' },
  { value: 'America/Denver', label: '(UTC-07:00) Mountain Time' },
  { value: 'America/Chicago', label: '(UTC-06:00) Central Time' },
  { value: 'America/New_York', label: '(UTC-05:00) Eastern Time' },
  { value: 'Europe/London', label: '(UTC+00:00) London' },
  { value: 'Europe/Brussels', label: '(UTC+01:00) Brussels' },
  { value: 'Europe/Helsinki', label: '(UTC+02:00) Helsinki' },
  { value: 'Asia/Kolkata', label: '(UTC+05:30) India' },
  { value: 'Asia/Shanghai', label: '(UTC+08:00) China' },
  { value: 'Asia/Tokyo', label: '(UTC+09:00) Tokyo' },
  { value: 'Australia/Sydney', label: '(UTC+10:00) Sydney' },
]

/** The product ships in English. The control stays so the setting has a home when more land. */
const LANGUAGES = [{ value: 'en', label: 'English' }]

const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  free: 'Free',
  premium: 'Premium',
  msp_premium: 'MSP Premium',
}

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  it_user: 'IT User',
  administrator: 'Administrator',
  owner: 'Owner',
  super_admin: 'Super Admin',
}

const BuildingIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
)

const CardIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
)

const MonitorIcon = () => (
  <svg className="h-4 w-4 shrink-0 text-theme-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
)

const KeyIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
  </svg>
)

const ShieldIcon = ({ on }: { on: boolean }) => (
  <svg
    className={`h-4 w-4 ${on ? 'text-emerald-400' : 'text-theme-text-tertiary'}`}
    fill="none" stroke="currentColor" viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
  </svg>
)

/** A tile in the tenant strip: icon, caption, value. */
function InfoTile({ icon, tone, label, value }: {
  icon: React.ReactNode
  tone: string
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-3">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-theme-text-tertiary">{label}</p>
        <div className="truncate font-semibold text-theme-text-primary">{value}</div>
      </div>
    </div>
  )
}

export default function Account() {
  const { user, tenantContext, updateUser, canManageSubscription, isTenantAdmin } = useAuthStore()
  // Two different authorities: an administrator runs the tenant, an owner also holds the
  // subscription. Only the second may reach the page that spends money.
  const canBuy = canManageSubscription()
  const isAdmin = isTenantAdmin()

  const [prefs, setPrefs] = useState<AccountPreferences | null>(null)
  const [mfa, setMfa] = useState<MFAStatus | null>(null)
  const [signIns, setSignIns] = useState<EventData[]>([])
  const [subscription, setSubscription] = useState<TenantSubscriptionStatus | null>(null)
  const [loading, setLoading] = useState(true)

  const [notice, setNotice] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null)
  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false)
  // Turning MFA off and reissuing recovery codes both re-ask for the password, so they share
  // one prompt rather than two near-identical dialogs.
  const [passwordPrompt, setPasswordPrompt] = useState<'disable-mfa' | 'recovery-codes' | null>(null)
  const [promptPassword, setPromptPassword] = useState('')
  const [promptBusy, setPromptBusy] = useState(false)
  const [promptError, setPromptError] = useState<string | null>(null)
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[] | null>(null)

  const say = (tone: 'ok' | 'bad', text: string) => setNotice({ tone, text })

  const refreshMfa = useCallback(async () => {
    const status = await getMFAStatus()
    setMfa(status)
    updateUser({ mfa_enabled: status.enabled })
  }, [updateUser])

  useEffect(() => {
    let live = true
    const load = async () => {
      // Each panel settles on its own: the sign-in list needs a permission a viewer does not
      // have, and the subscription needs one this tenant may not have configured. Neither
      // absence should empty the page.
      const [prefsR, mfaR, eventsR, subR] = await Promise.allSettled([
        getAccountPreferences(),
        getMFAStatus(),
        listEvents({ event_type: 'login_success', limit: 6 }),
        getTenantSubscriptionStatus(),
      ])
      if (!live) return
      if (prefsR.status === 'fulfilled') setPrefs(prefsR.value.preferences)
      if (mfaR.status === 'fulfilled') setMfa(mfaR.value)
      if (eventsR.status === 'fulfilled') {
        // Tenant-wide by permission; this card is about YOUR account, so keep your own rows.
        setSignIns(eventsR.value.events.filter((e) => e.user_email === user?.email))
      }
      if (subR.status === 'fulfilled') setSubscription(subR.value)
      setLoading(false)
    }
    load()
    return () => { live = false }
  }, [user?.email])

  const savePreference = async (patch: Partial<AccountPreferences>) => {
    const previous = prefs
    setPrefs((p) => (p ? { ...p, ...patch } : p))  // optimistic: a switch must move when pressed
    try {
      const { preferences } = await updateAccountPreferences(patch)
      setPrefs(preferences)
    } catch (e) {
      setPrefs(previous)
      say('bad', errorText(e, 'Could not save that preference.'))
    }
  }

  const saveName = async () => {
    const name = nameDraft.trim()
    if (!name || name === user?.full_name) {
      setEditingName(false)
      return
    }
    try {
      const updated = await updateProfile(name)
      updateUser({ full_name: updated.full_name })
      setEditingName(false)
      say('ok', 'Profile updated.')
    } catch (e) {
      say('bad', errorText(e, 'Could not update your profile.'))
    }
  }

  const submitPasswordPrompt = async (e: React.FormEvent) => {
    e.preventDefault()
    setPromptError(null)
    setPromptBusy(true)
    try {
      if (passwordPrompt === 'disable-mfa') {
        await disableMFA(promptPassword)
        await refreshMfa()
        say('ok', 'Two-factor authentication is off.')
      } else {
        const { recovery_codes } = await regenerateRecoveryCodes(promptPassword)
        await refreshMfa()
        setNewRecoveryCodes(recovery_codes)
      }
      setPasswordPrompt(null)
      setPromptPassword('')
    } catch (err) {
      setPromptError(errorText(err, 'That did not work.'))
    } finally {
      setPromptBusy(false)
    }
  }

  const onMfaToggle = (next: boolean) => {
    if (next) setMfaSetupOpen(true)
    else setPasswordPrompt('disable-mfa')
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24 text-theme-text-tertiary">
        <Spinner className="h-6 w-6" />
      </div>
    )
  }

  const fullName = user?.full_name || 'User'
  const expiresInDays = subscription?.days_until_expiry

  return (
    <PageShell>
      <div className="px-7 py-7">
        <PageHeader
          title="My Account"
          subtitle="Manage your profile, security and tenant settings."
        />

        {notice && (
          <div
            role="status"
            className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
              notice.tone === 'ok'
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                : 'border-red-500/30 bg-red-500/10 text-red-400'
            }`}
          >
            {notice.text}
          </div>
        )}

        <SectionCard title="Profile" className="mb-6">
          <div className="flex items-center gap-4">
            <Avatar name={fullName} size={56} src={user?.avatar_url} />
            <div className="min-w-0 flex-1">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    className="input max-w-xs"
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveName()
                      if (e.key === 'Escape') setEditingName(false)
                    }}
                    aria-label="Full name"
                  />
                  <button type="button" className="btn-blue btn-sm" onClick={saveName}>Save</button>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => setEditingName(false)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-theme-text-primary">{fullName}</span>
                  {tenantContext?.role && (
                    <Badge tone="blue">{ROLE_LABELS[tenantContext.role] || tenantContext.role}</Badge>
                  )}
                </div>
              )}
              <p className="text-sm text-theme-text-secondary">{user?.email}</p>
            </div>
            {!editingName && (
              <button
                type="button"
                className="btn-outline"
                onClick={() => { setNameDraft(fullName); setEditingName(true) }}
              >
                Edit profile
              </button>
            )}
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Language" compact>
              <select
                className="input"
                value={prefs?.language ?? 'en'}
                onChange={(e) => savePreference({ language: e.target.value })}
                // One option today. Left enabled rather than disabled so the control does not
                // read as broken; picking English is a no-op.
              >
                {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </Field>
            <Field label="Timezone" compact>
              <select
                className="input"
                value={prefs?.timezone ?? 'UTC'}
                onChange={(e) => savePreference({ timezone: e.target.value })}
              >
                {TIMEZONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
          </div>
        </SectionCard>

        {/* The reference pairs Security with a Notifications card. Ours is dropped rather than
            drawn: the product sends no packaging-result or deployment-failure mail, and a switch
            that only records a preference reads as a feature. Security takes the full width. */}
        <div className="mb-6">
          <SectionCard title="Security">
            <div className="space-y-4">
              <button
                type="button"
                className="btn-outline w-full justify-start"
                onClick={() => setChangePasswordOpen(true)}
              >
                <KeyIcon /> Change password
              </button>

              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <ShieldIcon on={!!mfa?.enabled} />
                  <div className="min-w-0">
                    <span className="block text-sm text-theme-text-primary">
                      Two-factor authentication
                    </span>
                    {mfa?.enabled && (
                      <span className="block text-xs text-theme-text-tertiary">
                        {mfa.recovery_codes_remaining} recovery code
                        {mfa.recovery_codes_remaining === 1 ? '' : 's'} left ·{' '}
                        <button
                          type="button"
                          className="text-blue-500 hover:text-blue-400"
                          onClick={() => setPasswordPrompt('recovery-codes')}
                        >
                          Regenerate
                        </button>
                      </span>
                    )}
                    {mfa && !mfa.available && (
                      <span className="block text-xs text-theme-text-tertiary">
                        Handled by your identity provider for SSO accounts
                      </span>
                    )}
                    {mfa?.required && (
                      <span className="block text-xs text-theme-text-tertiary">
                        {mfa.enabled
                          ? 'Required by your organization; it cannot be turned off.'
                          : 'Required by your organization; you will be asked to set it up at your next sign-in.'}
                      </span>
                    )}
                  </div>
                </div>
                <Toggle
                  checked={!!mfa?.enabled}
                  onChange={onMfaToggle}
                  // Turning it on is always open; turning it off is refused while required.
                  disabled={!mfa?.available || (!!mfa?.enabled && !!mfa?.required)}
                  label="Two-factor authentication"
                />
              </div>

            </div>
          </SectionCard>
        </div>

        <SectionCard title="Tenant & subscription" className="mb-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <InfoTile
              icon={<BuildingIcon />}
              tone="bg-blue-500/15 text-blue-300"
              label="Tenant"
              value={tenantContext?.tenant_name || '—'}
            />
            <InfoTile
              icon={<CardIcon />}
              tone="bg-violet-500/15 text-violet-300"
              label="Plan"
              value={subscription?.plan ? (PLAN_LABELS[subscription.plan] || subscription.plan) : '—'}
            />
            <InfoTile
              icon={<CardIcon />}
              tone="bg-amber-500/15 text-amber-300"
              label="Subscription"
              value={
                <span className={expiresInDays != null && expiresInDays <= 7 ? 'text-amber-300' : ''}>
                  {expiresInDays == null
                    ? (subscription?.status ? subscription.status : '—')
                    : expiresInDays > 0
                      ? `Expires in ${expiresInDays} day${expiresInDays === 1 ? '' : 's'}`
                      : 'Expired'}
                </span>
              }
            />
          </div>

          {/* Only the owner may spend on the subscription, so only the owner is sent to the page
              that does it. Everyone else reads the state above and takes it to whoever owns it. */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {canBuy ? (
              <Link to="/settings/subscription" className="btn-green">Manage subscription</Link>
            ) : (
              <p className="text-xs text-theme-text-tertiary">
                Contact your account owner to change the subscription.
              </p>
            )}
            {isAdmin && <Link to="/teams/users" className="btn-outline">Manage users</Link>}
          </div>
        </SectionCard>

        <SectionCard title="Recent sign-ins">
          {signIns.length === 0 ? (
            <p className="text-sm text-theme-text-secondary">No recent sign-ins.</p>
          ) : (
            <div className="space-y-2">
              {signIns.map((e) => (
                <div
                  key={e.id}
                  className="flex items-center gap-3 border-b border-theme-border-primary pb-2 text-sm last:border-0"
                >
                  <MonitorIcon />
                  <span className="text-theme-text-primary">{dateTime(e.created_at)}</span>
                  <span className="ml-auto text-theme-text-tertiary">
                    {e.ip_address ? `IP ${e.ip_address}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <ChangePasswordModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <MfaSetupModal
        open={mfaSetupOpen}
        onClose={() => setMfaSetupOpen(false)}
        onEnabled={() => { refreshMfa().catch(() => {}) }}
      />

      <Modal
        open={passwordPrompt !== null}
        onClose={() => { setPasswordPrompt(null); setPromptPassword(''); setPromptError(null) }}
        title={passwordPrompt === 'disable-mfa' ? 'Turn off two-factor authentication' : 'New recovery codes'}
        footer={
          <>
            <button
              type="button"
              className="btn-outline"
              onClick={() => { setPasswordPrompt(null); setPromptPassword(''); setPromptError(null) }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="account-password-prompt"
              className={passwordPrompt === 'disable-mfa' ? 'btn-danger' : 'btn-blue'}
              disabled={promptBusy || !promptPassword}
            >
              {promptBusy ? 'Working…' : passwordPrompt === 'disable-mfa' ? 'Turn off' : 'Generate'}
            </button>
          </>
        }
      >
        <form id="account-password-prompt" onSubmit={submitPasswordPrompt} className="space-y-3">
          <p className="text-sm text-theme-text-secondary">
            {passwordPrompt === 'disable-mfa'
              ? 'Your account will be protected by its password alone. Enter it to confirm.'
              : 'The codes you have now stop working. Enter your password to issue a new set.'}
          </p>
          {promptError && <p className="text-sm text-red-400">{promptError}</p>}
          <input
            type="password"
            className="input"
            autoComplete="current-password"
            value={promptPassword}
            onChange={(e) => setPromptPassword(e.target.value)}
            placeholder="Password"
            aria-label="Password"
          />
        </form>
      </Modal>

      <Modal
        open={newRecoveryCodes !== null}
        onClose={() => setNewRecoveryCodes(null)}
        title="Save your recovery codes"
        footer={
          <button type="button" className="btn-blue" onClick={() => setNewRecoveryCodes(null)}>
            I have saved them
          </button>
        }
      >
        <p className="mb-3 text-sm text-theme-text-secondary">
          Each code signs you in once. They are shown only now.
        </p>
        <ul className="grid grid-cols-2 gap-2 rounded-lg bg-theme-tertiary p-4 font-mono text-sm text-theme-text-primary">
          {(newRecoveryCodes || []).map((c) => (
            <li key={c} className="select-all text-center">{c}</li>
          ))}
        </ul>
      </Modal>
    </PageShell>
  )
}
